import { RestApiClient } from 'ifield-core-components'
import log from 'loglevel'
import _ from 'lodash'
import { FUNCTION_TYPE } from '@/constants'

/**
 * センサー測定項目取得
 * @param {Number} id ID
 * @returns {Array.<Object>|Object} データ
 */
const getSensorItems = async (id) => {
  const endPoint = id == null ? 'sensor_item' : `sensor_item/${id}`
  const url = RestApiClient.createApiUrl(endPoint)
  const json = await RestApiClient.executeGetApi(url)
  return json.data
}

/**
 * 初期スロットル間隔(ms)
 */
const INITIAL_THROTTLE_TIME = 30000

/**
 * 当モジュールで扱うデータが利用可能な権限タイプ
 */
const MY_FUNCTION_TYPE = FUNCTION_TYPE.SENSOR

export default {
  namespaced: true,
  state: {
    sensorItems: [],
    throttleTime: INITIAL_THROTTLE_TIME,
    throttler: null,
  },
  persistentStates: ['throttleTime'],
  mutations: {
    /**
     * stateを初期化する
     * @param {*} state
     */
    resetState(state) {
      state.sensorItems = []
      state.throttleTime = INITIAL_THROTTLE_TIME
      state.throttler = null
    },
    /**
     * センサー測定項目を設定する
     * @param {*} state
     * @param {Array.<Object>} sensorItems センサー測定項目
     */
    setSensorItems(state, sensorItems) {
      state.sensorItems = sensorItems
    },
    /**
     * 特定のセンサー測定項目を設定する
     * @param {*} state
     * @param {Object} sensorItem センサー測定項目
     */
    setSpecificSensorItem(state, sensorItem) {
      const targetIndex = state.sensorItems.findIndex(
        (l) => l.id === sensorItem.id
      )
      if (targetIndex >= 0) {
        state.sensorItems[targetIndex] = sensorItem
      } else {
        state.sensorItems.push(sensorItem)
      }
    },
    /**
     * スロットル間隔を保存する
     * @param {*} state
     * @param {Number} time スロットル間隔(ms)
     */
    setThrottleTime(state, time) {
      state.throttleTime = +time
    },
    /**
     * スロットル関数を保存する
     * @param {*} state
     * @param {Function} throttler スロットル関数
     */
    setThrottler(state, throttler) {
      state.throttler = throttler
    },
  },
  getters: {
    /**
     * センサー測定項目
     * @param {*} state
     * @returns {Array.<Object>}
     */
    sensorItems: (state) => state.sensorItems,
    /**
     * センサー測定項目(Map)
     * @param {*} state
     * @returns {Map}
     */
    sensorItemMap: (state) =>
      new Map(
        state.sensorItems.map((sensorItem) => [sensorItem.id, sensorItem])
      ),
    /**
     * 特定IDのセンサー測定項目を取得
     * @param {*} state
     * @param {*} getters
     * @returns {Function} IDを引数に特定IDのセンサー測定項目を返す関数
     */
    getSensorItemById: (state, getters) => (id) =>
      getters.sensorItemMap.get(id),
  },
  actions: {
    /**
     * stateをリセットする
     */
    resetState({ commit }) {
      commit('resetState')
    },
    /**
     * SensorItemを更新する
     */
    async updateSensorItems({ commit, rootGetters }) {
      if (!rootGetters['functionType'](MY_FUNCTION_TYPE)) {
        log.info('function type: sensor is unavaliable. skip update')
        return
      }
      const sensorItems = await getSensorItems()
      commit('setSensorItems', sensorItems)
    },
    /**
     * 特定のSensorItemを更新する
     * @param {Number} id 対象のセンサー測定項目ID
     */
    async updateSpecificSensorItem({ commit, rootGetters }, id) {
      if (!rootGetters['functionType'](MY_FUNCTION_TYPE)) {
        log.info('function type: sensor is unavaliable. skip update')
        return
      }
      const sensorItem = await getSensorItems(id)
      commit('setSpecificSensorItem', sensorItem)
      return sensorItem
    },
    /**
     * センサー測定項目を更新する(スロットル)
     */
    async updateSensorItemsWithThrottle({ state, commit, dispatch }) {
      if (!state.throttler) {
        const throttler = _.throttle(
          () => dispatch('updateSensorItems'),
          state.throttleTime,
          { trailing: false }
        )
        commit('setThrottler', throttler)
      }
      return state.throttler()
    },
    /**
     * スロットル間隔更新
     * @param {Number} time スロットル間隔(ms)
     */
    updateThrottleTime({ commit }, time) {
      commit('setThrottleTime', +time)
      commit('setThrottler', null)
    },
  },
}
